<template>
    <div class="card-box">
        <div class="card-title">
            <span class="card-title-text">{{ card.name || '未命名卡片' }}</span>
            <el-icon class="card-zoom" title="查看详情" @click="dialogVisible = true"><Search /></el-icon>
        </div>
        <div class="card-body">
            <el-table v-if="rows.length" :data="rows" size="small" height="240">
                <el-table-column v-for="col in columns" :key="col.prop" :prop="col.prop" :label="col.label" show-overflow-tooltip />
            </el-table>
            <div v-else class="card-placeholder">暂无数据</div>
        </div>

        <!-- 弹框展开：使用 vxe-table，支持列设置（显隐/拖拽排序/冻结列）、表头排序、列头筛选 -->
        <el-dialog v-model="dialogVisible" :title="card.name || '未命名卡片'"
            :width="fullscreen ? '96vw' : 'min(1200px, 94vw)'" :fullscreen="fullscreen" top="6vh"
            :close-on-click-modal="false" class="card-table-dialog" @opened="onDialogOpened"
            @closed="fullscreen = false">
            <div class="card-table-toolbar">
                <el-input v-model="keyword" class="card-table-search" size="small" clearable placeholder="搜索（匹配所有列）">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>
                <span class="card-table-match">匹配 {{ viewRows.length }} / {{ rows.length }} 行</span>
                <span class="card-table-tips">点击表头排序，列头漏斗筛选；「列设置」支持显隐、拖拽排序、冻结列</span>
                <div class="card-table-actions">
                    <el-button size="small" @click="openColumnSetting">
                        <el-icon class="el-icon--left">
                            <Setting />
                        </el-icon>
                        列设置
                    </el-button>
                    <el-button size="small" @click="fullscreen = !fullscreen">
                        <el-icon class="el-icon--left">
                            <FullScreen />
                        </el-icon>
                        {{ fullscreen ? '退出全屏' : '全屏' }}
                    </el-button>
                </div>
            </div>
            <vxe-table ref="xTable" class="card-table-vxe" :data="viewRows" :height="tableHeight" size="small" border
                stripe show-overflow auto-resize :row-config="{ keyField: '__rowKey', isHover: true }"
                :column-config="{ resizable: true }"
                :sort-config="{ trigger: 'cell', orders: ['asc', 'desc', null] }"
                :filter-config="{ showIcon: true }"
                :custom-config="{ trigger: 'manual', mode: 'popup', allowFixed: true, allowSort: true, immediate: true, storage: false }"
                :scroll-y="{ enabled: true, gt: 50 }">
                <vxe-column type="seq" width="60" title="#" fixed="left" />
                <vxe-column v-for="col in columns" :key="col.prop" :field="col.prop" :title="col.label"
                    :min-width="col.minWidth" :sortable="true" :sort-by="col.sortBy" :filters="col.filters"
                    :filter-method="filterMethod" />
                <template #empty>
                    <div class="card-table-empty">暂无数据</div>
                </template>
            </vxe-table>
        </el-dialog>
    </div>
</template>

<script>
import { Search } from '@element-plus/icons';
import request from '@/utils/request';

export default {
    name: 'CardTable',
    components: { Search },
    props: {
        card: { type: Object, required: true },
        viewId: { type: String, default: '' },
        sectionIndex: { type: Number, default: -1 },
        cardIndex: { type: Number, default: -1 }
    },
    data() {
        return {
            rows: [],
            columns: [],
            dialogVisible: false,
            // 弹框内全局搜索关键字、全屏态
            keyword: '',
            fullscreen: false,
            // 视口高度：vxe-table 的 height 需要具体像素值（vh 不生效会导致表格过矮）
            viewportHeight: window.innerHeight
        };
    },
    computed: {
        // 弹框表格数据：关键字跨所有列过滤
        viewRows() {
            const key = this.keyword.trim().toLowerCase();
            if (!key) return this.rows;
            const props = this.columns.map(c => c.prop);
            const match = (v) => v !== null && v !== undefined && String(v).toLowerCase().includes(key);
            return this.rows.filter(row => {
                if (!row) return false;
                if (!props.length) {
                    return Object.keys(row).some(k => k !== '__rowKey' && match(row[k]));
                }
                return props.some(p => match(row[p]));
            });
        },
        // 表格高度：随窗口与全屏状态换算为像素
        tableHeight() {
            const ratio = this.fullscreen ? 0.76 : 0.68;
            return Math.max(360, Math.round(this.viewportHeight * ratio));
        }
    },
    mounted() {
        this.loadData();
        window.addEventListener('resize', this.onWindowResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onWindowResize);
    },
    methods: {
        onWindowResize() {
            this.viewportHeight = window.innerHeight;
        },
        loadData() {
            if (!this.viewId || this.sectionIndex < 0 || this.cardIndex < 0) {
                return;
            }
            return request({
                url: '/dataview/getCardData',
                method: 'post',
                data: {
                    id: this.viewId,
                    sectionIndex: this.sectionIndex,
                    cardIndex: this.cardIndex
                }
            }).then(res => {
                if (res.code === 0) {
                    this.parseData(res.meta.data);
                }
            });
        },
        parseData(data) {
            this.keyword = '';
            if (!data) {
                this.columns = [];
                this.rows = [];
                return;
            }
            const configuredColumns = this.parseColumnConfig();
            // 数组：每项为一行对象，列名取首行对象的 key
            if (Array.isArray(data)) {
                this.rows = data.map((row, index) => this.withRowKey(row, index));
                if (configuredColumns) {
                    this.columns = configuredColumns;
                } else if (data.length > 0 && data[0] && typeof data[0] === 'object') {
                    this.columns = Object.keys(data[0]).map(k => ({ prop: k, label: k }));
                } else {
                    this.columns = [];
                }
                this.buildColumnConfig();
                return;
            }
            // 对象：支持 { columns: [...], rows: [...] } 结构
            if (typeof data === 'object') {
                if (configuredColumns) {
                    this.columns = configuredColumns;
                } else {
                    const rawColumns = data.columns || [];
                    this.columns = rawColumns.map(c => (typeof c === 'string' ? { prop: c, label: c } : c));
                }
                this.rows = (data.rows || []).map((row, index) => this.withRowKey(row, index));
                this.buildColumnConfig();
            }
        },
        // vxe-table 的 row-config.keyField 需要唯一键，给每行补一个内部行号
        withRowKey(row, index) {
            return row && typeof row === 'object' ? Object.assign({}, row, { __rowKey: index }) : { value: row, __rowKey: index };
        },
        // 为弹框表格构建列附加信息：筛选项（按列去重，取值过多则不提供）与数值列排序
        buildColumnConfig() {
            const MAX_FILTER_OPTIONS = 50;
            this.columns = this.columns.map(col => {
                const values = new Map();
                let overflow = false;
                let numeric = this.rows.length > 0;
                this.rows.forEach(row => {
                    const raw = row ? row[col.prop] : null;
                    if (raw !== null && raw !== undefined && raw !== '' && isNaN(Number(raw))) {
                        numeric = false;
                    }
                    if (overflow) return;
                    const key = raw === null || raw === undefined ? '' : String(raw);
                    if (values.has(key)) return;
                    if (values.size >= MAX_FILTER_OPTIONS) {
                        overflow = true;
                        return;
                    }
                    values.set(key, true);
                });
                const extra = {};
                // 取值过多（如时间、ID）不提供列头筛选，避免出现不完整的选项
                if (!overflow && values.size) {
                    extra.filters = Array.from(values.keys())
                        .sort((a, b) => a.localeCompare(b, 'zh-CN', { numeric: true }))
                        .map(v => ({ label: v === '' ? '（空）' : v, value: v }));
                }
                // 数值列按数值排序，避免字符串比较导致 10 排在 2 前面
                if (numeric) {
                    extra.sortBy = row => Number(row[col.prop]);
                }
                // 列宽只在这里算一次，避免搜索重渲染时反复遍历全量数据
                extra.minWidth = this.calcColumnWidth(col);
                return Object.assign({}, col, extra);
            });
        },
        // vxe 列头筛选：选中的取值需与单元格文本完全一致（过滤器值均为字符串）
        filterMethod({ value, row, column }) {
            const list = Array.isArray(value) ? value : [value];
            if (!list.length) return true;
            const raw = row ? row[column.field] : null;
            const text = raw === null || raw === undefined ? '' : String(raw);
            return list.some(v => String(v) === text);
        },
        // 打开 vxe 自带的列设置面板：显隐、拖拽排序、左/右冻结、列宽
        openColumnSetting() {
            const table = this.$refs.xTable;
            if (table && table.openCustom) {
                table.openCustom();
            }
        },
        // 弹框打开后再计算一次布局（避免打开动画期间列宽异常）
        onDialogOpened() {
            this.$nextTick(() => {
                const table = this.$refs.xTable;
                if (table && table.recalculate) table.recalculate(true);
            });
        },
        // 解析参数中的 column 配置：createTime:创建时间,machineId:设备ID,freePhysicalMemorySize
        // 有冒号时冒号后为展示名称，无冒号时用列名作为展示名称；按配置顺序展示
        parseColumnConfig() {
            const params = this.card.params || {};
            const column = params.column;
            if (typeof column !== 'string' || !column.trim()) {
                return null;
            }
            return column.split(',')
                .map(s => s.trim())
                .filter(Boolean)
                .map(token => {
                    const idx = token.indexOf(':');
                    if (idx >= 0) {
                        const prop = token.slice(0, idx).trim();
                        const label = token.slice(idx + 1).trim();
                        return { prop, label: label || prop };
                    }
                    return { prop: token, label: token };
                })
                .filter(c => c.prop);
        },
        calcColumnWidth(col) {
            const measure = (s) => {
                let len = 0;
                for (const ch of s) {
                    len += /[\u4e00-\u9fff]/.test(ch) ? 2 : 1;
                }
                return len;
            };
            let maxLen = measure(String(col.label || ''));
            this.rows.forEach(row => {
                const v = row[col.prop];
                if (v != null) {
                    const len = measure(String(v));
                    if (len > maxLen) maxLen = len;
                }
            });
            // 每单位约 7px，加左右内边距，限制在 120~360px（留出排序、筛选图标的空间）
            return Math.min(360, Math.max(120, maxLen * 7 + 28));
        }
    }
};
</script>

<style scoped>
.card-box {
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
}
.card-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    font-size: 14px;
    font-weight: bold;
    border-bottom: 1px solid #f0f2f5;
    background: #fafafa;
    color: #303133;
}
.card-title-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.card-zoom {
    cursor: pointer;
    color: #909399;
    font-size: 16px;
    flex-shrink: 0;
}
.card-zoom:hover {
    color: #409eff;
}
/* 与折线图等图表卡片的 body 保持一致（240px + 8px 内边距），同一行卡片高度对齐 */
.card-body {
    height: 240px;
    padding: 8px;
}
.card-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #909399;
    font-size: 13px;
    border: 1px dashed #dcdfe6;
    border-radius: 6px;
}
/* 弹框工具条：搜索、匹配数、操作按钮 */
.card-table-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}
.card-table-search {
    width: 240px;
    flex: none;
}
.card-table-match {
    flex: none;
    font-size: 12px;
    color: #7d8ba1;
}
.card-table-tips {
    overflow: hidden;
    font-size: 12px;
    color: #aab4c4;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.card-table-actions {
    display: flex;
    flex: none;
    gap: 8px;
    margin-left: auto;
}
.card-table-actions .el-button {
    margin-left: 0;
}
.card-table-empty {
    padding: 24px 0;
    color: #909399;
    font-size: 13px;
}
</style>

<!-- 弹框挂载在 body 下，样式放非 scoped 块并统一前缀 -->
<style>
.card-table-dialog .el-dialog__body {
    padding-top: 8px;
}
</style>
